/**
 * Payment Context
 * Manages Stripe payment state and operations
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { getReferralDataForConversion, trackPurchaseConversion } from '../utils/affiliateTracking';
import { authFetch } from '../utils/api';
import { readAccessToken } from '../utils/accessToken';
import { getAcquisition } from '../utils/acquisition';
import { getAdContext } from '../utils/pixels';
import { isEmbedded } from '../utils/embedded';
import { toUserFacingError, ERROR_FALLBACKS } from '../utils/apiErrorHandler';

const PaymentContext = createContext(null);

// Apple App Store Guideline 3.1.1: no external (Stripe) payment for digital
// goods from inside the native app. Inside the WebView shell we refuse to
// redirect to Stripe and tell the user to manage billing on the open web.
const EMBEDDED_PAYMENT_MESSAGE =
  'To upgrade or manage billing, open canira.io in your web browser — purchases aren’t available inside the app.';

// Initialize Stripe with public key (only if configured).
// Guideline 3.1.1: inside the native app shell we don't load the Stripe SDK
// (js.stripe.com) at all — there's no in-app purchase, so the script is dead
// weight and we want zero Stripe traffic for App Review. Nothing consumes this
// context stripePromise for rendering (course/page-builder checkouts load their
// own), so null is safe in the embedded shell.
const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = stripePublicKey && stripePublicKey !== 'pk_test_your_stripe_publishable_key' && !isEmbedded()
  ? loadStripe(stripePublicKey)
  : null;

if (!stripePromise && !isEmbedded()) {
  console.warn('Stripe not configured. Set REACT_APP_STRIPE_PUBLIC_KEY in .env to enable payments.');
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function PaymentProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Get auth token from localStorage (may be null for cookie-only Google OAuth sessions)
  const getAuthToken = () => readAccessToken();

  // Fetch available plans
  const fetchPlans = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/payments/plans`);
      const data = await response.json();
      if (data.plans) {
        setPlans(data.plans);
      }
      return data.plans;
    } catch (err) {
      console.error('Error fetching plans:', err);
      return [];
    }
  }, []);

  // Fetch current subscription
  const fetchSubscription = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return null;

    try {
      setLoading(true);
      const response = await authFetch(`${API_BASE}/payments/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setSubscription(data.subscription);
      return data;
    } catch (err) {
      console.error('Error fetching subscription:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch billing history
  const fetchBillingHistory = useCallback(async (limit = 10) => {
    const token = getAuthToken();
    if (!token) return [];

    try {
      const response = await authFetch(`${API_BASE}/payments/billing-history?limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setBillingHistory(data.invoices || []);
      return data.invoices || [];
    } catch (err) {
      console.error('Error fetching billing history:', err);
      return [];
    }
  }, []);

  // Fetch payment methods
  const fetchPaymentMethods = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return [];

    try {
      const response = await authFetch(`${API_BASE}/payments/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setPaymentMethods(data.paymentMethods || []);
      return data.paymentMethods || [];
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      return [];
    }
  }, []);

  // Validate a coupon code
  const validateCoupon = useCallback(async (code, type = 'subscription', planKey = null, amount = null) => {
    const token = getAuthToken();
    if (!token) {
      return { valid: false, reason: 'Please log in' };
    }

    try {
      const response = await authFetch(`${API_BASE}/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code, type, planKey, amount })
      });

      const data = await response.json();

      if (!response.ok) {
        return { valid: false, reason: data.error || 'Invalid coupon' };
      }

      return data;
    } catch (err) {
      console.error('Coupon validation error:', err);
      return { valid: false, reason: 'Failed to validate coupon' };
    }
  }, []);

  // Create checkout session and redirect to Stripe
  // extra.workshopOffer: the signed coach-workshop attendee link (30-day
  // trial). Only the offer page passes it, so /pricing always buys what it quotes.
  const createCheckout = useCallback(async (planKey, successUrl, cancelUrl, couponCode = null, interval = 'month', foundingMember = false, extra = {}) => {
    // Guideline 3.1.1: never route to Stripe from inside the native app shell.
    if (isEmbedded()) {
      setError(EMBEDDED_PAYMENT_MESSAGE);
      return null;
    }
    const token = getAuthToken();
    if (!token) {
      setError('Please log in to upgrade');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Include affiliate referral data if present
      const referralData = getReferralDataForConversion();

      const response = await authFetch(`${API_BASE}/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planKey,
          successUrl,
          cancelUrl,
          couponCode,
          interval,
          foundingMember,
          adContext: getAdContext(getAcquisition()),
          ...(extra?.workshopOffer ? { workshopOffer: extra.workshopOffer } : {}),
          ...referralData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }

      return data;
    } catch (err) {
      console.error('[Payment] error:', err);
      setError(toUserFacingError(err, ERROR_FALLBACKS.payment));
      console.error('Checkout error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Open billing portal
  const openBillingPortal = useCallback(async (returnUrl) => {
    // Guideline 3.1.1: the Stripe billing portal is an external payment surface.
    if (isEmbedded()) {
      setError(EMBEDDED_PAYMENT_MESSAGE);
      return null;
    }
    const token = getAuthToken();
    if (!token) {
      setError('Please log in to manage billing');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`${API_BASE}/payments/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ returnUrl })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open billing portal');
      }

      // Redirect to Stripe Portal
      if (data.url) {
        window.location.href = data.url;
      }

      return data;
    } catch (err) {
      console.error('[Payment] error:', err);
      setError(toUserFacingError(err, ERROR_FALLBACKS.payment));
      console.error('Portal error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Cancel subscription
  const cancelSubscription = useCallback(async (cancelImmediately = false) => {
    const token = getAuthToken();
    if (!token) {
      setError('Please log in');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`${API_BASE}/payments/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ cancelImmediately })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      // Refresh subscription data
      await fetchSubscription();

      return data;
    } catch (err) {
      console.error('[Payment] error:', err);
      setError(toUserFacingError(err, ERROR_FALLBACKS.payment));
      console.error('Cancel error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchSubscription]);

  // Resume subscription
  const resumeSubscription = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setError('Please log in');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`${API_BASE}/payments/resume-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resume subscription');
      }

      // Refresh subscription data
      await fetchSubscription();

      return data;
    } catch (err) {
      console.error('[Payment] error:', err);
      setError(toUserFacingError(err, ERROR_FALLBACKS.payment));
      console.error('Resume error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchSubscription]);

  // Update subscription plan
  const updateSubscription = useCallback(async (newPlanKey) => {
    const token = getAuthToken();
    if (!token) {
      setError('Please log in');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`${API_BASE}/payments/update-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPlanKey })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update subscription');
      }

      // Refresh subscription data
      await fetchSubscription();

      return data;
    } catch (err) {
      console.error('[Payment] error:', err);
      setError(toUserFacingError(err, ERROR_FALLBACKS.payment));
      console.error('Update error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchSubscription]);

  // Create webinar payment
  const createWebinarPayment = useCallback(async (webinarId, amount, description, successUrl, cancelUrl, couponCode = null) => {
    // Guideline 3.1.1: paid-webinar checkout is an external payment surface too.
    if (isEmbedded()) {
      setError(EMBEDDED_PAYMENT_MESSAGE);
      return null;
    }
    const token = getAuthToken();
    if (!token) {
      setError('Please log in to purchase');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Include affiliate referral data if present
      const referralData = getReferralDataForConversion();

      const response = await authFetch(`${API_BASE}/payments/create-webinar-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          webinarId,
          amount,
          description,
          successUrl,
          cancelUrl,
          couponCode,
          ...referralData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }

      return data;
    } catch (err) {
      console.error('[Payment] error:', err);
      setError(toUserFacingError(err, ERROR_FALLBACKS.payment));
      console.error('Payment error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    // State
    loading,
    error,
    subscription,
    plans,
    billingHistory,
    paymentMethods,
    stripePromise,

    // Actions
    fetchPlans,
    fetchSubscription,
    fetchBillingHistory,
    fetchPaymentMethods,
    validateCoupon,
    createCheckout,
    openBillingPortal,
    cancelSubscription,
    resumeSubscription,
    updateSubscription,
    createWebinarPayment,
    clearError
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
}

export function usePayment() {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
}

export default PaymentContext;
